import './mask-field-ref.scss'

import { defineComponent, PropType } from 'vue'
import _ from 'lodash';

import { EntityField, EntityFieldStatus } from '@skinsoft/types';

import { reactiveVueMixin } from '@skinsoft/reactive-vue';
import { EntitySelect } from '@skinsoft/ui';
import { first } from 'rxjs/operators';

export const MaskFieldRef = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sEntitySelect: EntitySelect,
    },
    props: {
        entityField: {
            type: Object as PropType<EntityField>,
            required: true,
        },
        // fieldValue holds the raw stored value for this record-mask field, whose shape depends on
        // the entity field's own type (string, ref object, ...), so a broad type is the honest one.
        fieldValue: {} as { type?: PropType<any> },
        editing: {
            type: Boolean,
        },
        getStatus: {
            type: Function as PropType<(entityField: EntityField) => EntityFieldStatus>,
            required: true,
        },
        dataCy: {
            type: String,
        },
    },
    template: require('./mask-field-ref.html'),
    data() {
        return {
            fieldLabel: "",
            entitySelectKey: "", // used for reactivity issue with EntitySelect
        }
    },
    mounted() {
        this.updateKey()
    },
    computed: {
        value(): String {
            return _.get(this.fieldValue, 'ref') ?? this.fieldValue ?? ''
        },
    },
    methods: {
        onEntitySelected(entity: any) {
            this.$emit("value-change", {
                abstractEntity: this.entityField.abstractEntity,
                fieldPath: this.entityField.path,
                value: entity.id,
            })
        },
        thesaurusIdentifierIfNeeded(entityField: EntityField) {
            if (entityField.type.type === "ThesaurusTerm") {
                return entityField.type.thesaurusIdentifier
            }

            if (entityField.type.entityFieldType?.type === "ThesaurusTerm") {
                return entityField.type.entityFieldType.thesaurusIdentifier
            }

            return null
        },
        fetchLabel(key: string) {
            let translationKey: string = `field.${key}`
            if (this.entityField.entityFields
                && this.entityField.entityFields.length > 0) {
                translationKey += ".complex"
            }
            this.subscribeTo(
                this.$translationDataStore.getMessage(translationKey, this.getAppLocale()).pipe(first()),
                translation => {
                    this.fieldLabel = translation
                },
                'translation-subscription'
            )
        },
        isReadonly(entityField: EntityField) {
            return entityField.isReadOnly || this.getStatus(entityField).disabled
        },
        infoMessage(entityField: EntityField): string | undefined {
            return this.getStatus(entityField).disabledMessage
        },
        errorMessage(entityField: EntityField): string | undefined {
            return this.getStatus(entityField).errorMessage
        },
        hasError(entityField: EntityField) {
            return this.getStatus(entityField).error
        },
        // used for reactivity issue with EntitySelect
        updateKey() {
            if(this.fieldValue instanceof String || typeof this.fieldValue === "string") {
                this.entitySelectKey = this.fieldValue as string
                return
            }

            this.entitySelectKey = JSON.stringify(this.fieldValue)
        },
    },
    watch: {
        entityField: {
            handler() {
                this.unsubscribeFrom('translation-subscription')
                this.fetchLabel(this.entityField.path);
            },
            immediate: true,
            deep: false,
        },
        fieldValue() {
            this.updateKey()
        },
    },
})
